import React, { useState, useEffect } from 'react';
import { achievementApi } from '../services/api';
import './AchievementSection.css';

interface Achievement {
  achievement_id: string;
  title: string;
  description: string;
  type: string;
  target_value: number;
  points: number;
  icon: string;
  unlocked_at?: string;
  current_progress?: number;
}

const AchievementSection: React.FC = () => {
  const [achievements, setAchievements] = useState<Achievement[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadAchievements = async (retryCount = 0) => {
      try {
        const response = await achievementApi.getMyAchievements();
        if (response.success) {
          setAchievements((response.data as Achievement[]) || []);
        } else {
          setError('업적을 불러오는데 실패했습니다.');
        }
      } catch (err: any) {
        console.log('Achievement API error:', err);
        
        // ResourceNotFoundException이고 첫 번째 시도면 잠시 후 재시도
        if (err.response?.status === 500 && retryCount === 0 && 
            err.response?.data?.error?.message?.includes('ResourceNotFoundException')) {
          console.log('테이블 초기화 중... 3초 후 재시도');
          setTimeout(() => loadAchievements(1), 3000);
          return;
        }
        
        // 에러 발생 시 빈 배열로 설정
        setAchievements([]);
        setError('업적 데이터를 불러오는 중 오류가 발생했습니다.');
      } finally {
        setIsLoading(false);
      }
    };

    loadAchievements();
  }, []);

  const getProgressPercentage = (achievement: Achievement) => {
    if (achievement.unlocked_at) return 100;
    if (!achievement.current_progress) return 0;
    return Math.min((achievement.current_progress / achievement.target_value) * 100, 100);
  };

  if (isLoading) {
    return (
      <div className="achievement-section">
        <h3>내 업적</h3>
        <div className="achv-loading">업적을 불러오는 중...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="achievement-section">
        <h3>내 업적</h3>
        <div className="achv-error">{error}</div>
      </div>
    );
  }

  const completedAchievements = achievements.filter(a => a.unlocked_at);
  const inProgressAchievements = achievements.filter(a => !a.unlocked_at && (a.current_progress || 0) > 0);
  const notStartedAchievements = achievements.filter(a => !a.unlocked_at && (a.current_progress || 0) === 0);

  return (
    <div className="achievement-section">
      <div className="achievement-header">
        <h3>내 업적</h3>
        <div className="achievement-stats">
          <span className="completed-count">{completedAchievements.length}</span>
          <span className="total-count">/ {achievements.length} 완료</span>
        </div>
      </div>

      {completedAchievements.length > 0 && (
        <div className="achievement-group">
          <h4 className="group-title">🏆 완료된 업적</h4>
          <div className="achievement-list">
            {completedAchievements.map((achievement) => (
              <div key={achievement.achievement_id} className="achievement-item completed">
                <span className="achievement-icon">{achievement.icon}</span>
                <div className="achievement-content">
                  <span className="achievement-title">{achievement.title}</span>
                  <span className="achievement-desc">{achievement.description}</span>
                </div>
                <div className="achievement-reward">+{achievement.points}점</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {inProgressAchievements.length > 0 && (
        <div className="achievement-group">
          <h4 className="group-title">⏳ 진행 중인 업적</h4>
          <div className="achievement-list">
            {inProgressAchievements.map((achievement) => (
              <div key={achievement.achievement_id} className="achievement-item in-progress">
                <span className="achievement-icon">{achievement.icon}</span>
                <div className="achievement-content">
                  <span className="achievement-title">{achievement.title}</span>
                  <span className="achievement-desc">{achievement.description}</span>
                  <div className="progress-info">
                    <span className="progress-text">{achievement.current_progress} / {achievement.target_value}</span>
                    <div className="progress-bar">
                      <div className="progress-fill" style={{ width: `${getProgressPercentage(achievement)}%` }}></div>
                    </div>
                  </div>
                </div>
                <div className="achievement-reward">+{achievement.points}점</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {notStartedAchievements.length > 0 && (
        <div className="achievement-group">
          <h4 className="group-title">📋 미완료 업적</h4>
          <div className="achievement-list">
            {notStartedAchievements.map((achievement) => (
              <div key={achievement.achievement_id} className="achievement-item not-started">
                <span className="achievement-icon">{achievement.icon}</span>
                <div className="achievement-content">
                  <span className="achievement-title">{achievement.title}</span>
                  <span className="achievement-desc">{achievement.description}</span>
                </div>
                <div className="achievement-reward">+{achievement.points}점</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default AchievementSection;